import { MARKET_ASSET_CLASS_LABELS } from '../products/productConstants';
import {
  FUTURES_ASSET_CLASS_OPTIONS,
  FUTURES_OVERRIDE_OPTIONS,
  FUTURES_ROUND_PERIODS,
  FUTURES_ROUND_PERIOD_LABELS,
  OVERRIDE_LABELS,
  inputDateTimeToQueryApi,
  toInputDateTime,
} from './futuresRoundConstants';

/** 訂單狀態 */
export const FUTURES_ORDER_STATE_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'BOOKED', label: '待開獎' },
  { value: 'SETTLED', label: '已完成' },
  { value: 'CANCELLED', label: '已撤單' },
];

export const FUTURES_ORDER_STATE_LABELS = {
  BOOKED: '待開獎',
  SETTLED: '已完成',
  CANCELLED: '已撤單',
};

/** 投注類型 */
export const FUTURES_ORDER_TREND_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'UP', label: '做多' },
  { value: 'DOWN', label: '做空' },
  { value: 'FLAT', label: '持平' },
];

export const FUTURES_ORDER_TREND_LABELS = {
  UP: '做多',
  DOWN: '做空',
  FLAT: '持平',
};

/** 帳號類型 */
export const MEMBER_ROLE_OPTIONS = [
  { value: '', label: '全部' },
  { value: '1', label: '正常帳號' },
  { value: '2', label: '試玩帳號' },
];

export const MEMBER_ROLE_LABELS = {
  1: '正常帳號',
  2: '試玩帳號',
  3: '測試帳號',
};

const MAX_ORDER_QUERY_DAYS = 30;

function pad2(n) {
  return String(n).padStart(2, '0');
}

export function defaultOrderTimeRange() {
  const now = new Date();
  const to = new Date(now);
  to.setHours(23, 59, 0, 0);

  const from = new Date(now);
  from.setDate(from.getDate() - 16);
  from.setHours(0, 0, 0, 0);

  return {
    orderTimeFromInput: toInputDateTime(from),
    orderTimeToInput: toInputDateTime(to),
  };
}

export function formatAmount(value) {
  if (value == null || value === '') {
    return '—';
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return String(value);
  }
  return num.toFixed(2);
}

export function formatPrice(value, digits = 6) {
  if (value == null || value === '') {
    return '—';
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return '—';
  }
  return num.toFixed(digits);
}

export function formatOddsPercent(value) {
  if (value == null || value === '') {
    return '—';
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return '—';
  }
  if (num <= 2) {
    return `${(num * 100).toFixed(2)}%`;
  }
  return `${num.toFixed(2)}%`;
}

export function formatBettingRatio(value) {
  if (value == null || value === '') {
    return '—';
  }
  const num = Number(value);
  if (Number.isNaN(num)) {
    return '—';
  }
  return `${num.toFixed(6)}%`;
}

export function parseOverrideMethodLabel(method) {
  if (!method) {
    return '—';
  }
  const key = String(method);
  const mapped = {
    GLOBAL_CONTROL: '系統控',
    GO_LONG: '做多贏',
    GO_SHORT: '做空贏',
    PROMISE_PROFIT: '系統控-殺大',
    DO_NOTHING: '系統控-無',
    USER: '單控',
    DEFAULT: '跟隨全局',
  };
  return mapped[key] || OVERRIDE_LABELS[key] || key;
}

export function formatPhoneEmail(row) {
  if (!row) {
    return '—';
  }
  const contact = row.phoneEmail || row.mobile || row.email;
  if (!contact) {
    return '—';
  }
  if (String(contact).includes('@')) {
    return contact;
  }
  const code = row.areaCode ? `+${row.areaCode} ` : '';
  return `${code}${contact}`;
}

export function formatAreaCode(row) {
  if (!row) {
    return '—';
  }
  const contact = row.phoneEmail || row.mobile || row.email || '';
  if (String(contact).includes('@')) {
    return '—';
  }
  return row.areaCode ? `+${row.areaCode}` : '—';
}

export function formatOrderResultText(row) {
  if (!row) {
    return '—';
  }
  if (row.state === 'CANCELLED') {
    return '已撤單';
  }
  if (row.state === 'BOOKED') {
    return '—';
  }
  const trend =
    FUTURES_ORDER_TREND_LABELS[row.settleTrend] ||
    FUTURES_ORDER_TREND_LABELS[row.orderTrend] ||
    row.settleTrend ||
    '—';
  const buyPrice = formatPrice(row.startPrice ?? row.orderPrice);
  const settlePrice = formatPrice(row.settlePrice);
  return `${trend}\n買入價格：${buyPrice}\n結算價格：${settlePrice}`;
}

function calendarDaysBetween(fromInput, toInput) {
  const fromDate = fromInput.slice(0, 10);
  const toDate = toInput.slice(0, 10);
  const from = new Date(`${fromDate}T00:00:00`);
  const to = new Date(`${toDate}T00:00:00`);
  return Math.round((to - from) / (24 * 60 * 60 * 1000));
}

export function validateOrderTimeRange(fromInput, toInput) {
  if (!fromInput || !toInput) {
    return '請選擇委託時間起迄';
  }
  const from = new Date(inputDateTimeToQueryApi(fromInput));
  const to = new Date(inputDateTimeToQueryApi(toInput));
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return '委託時間格式不正確';
  }
  if (from > to) {
    return '委託時間起不可晚於迄';
  }
  const days = calendarDaysBetween(fromInput, toInput);
  if (days > MAX_ORDER_QUERY_DAYS) {
    return `委託時間跨度不可超過 ${MAX_ORDER_QUERY_DAYS} 天`;
  }
  return null;
}

export function buildPageParams(pageNo, filters) {
  const params = { pageNo, pageSize: 15 };

  if (filters.state) {
    params.state = filters.state;
  }
  if (filters.orderTrend) {
    params.orderTrend = filters.orderTrend;
  }
  if (filters.roleId) {
    params.roleId = Number(filters.roleId);
  }
  const orderSerialId = filters.orderSerialId?.trim();
  if (orderSerialId) {
    params.orderSerialId = orderSerialId;
  }
  const roundCode = filters.roundCode?.trim();
  if (roundCode) {
    params.roundCode = roundCode;
  }
  const memberIdsCsv = filters.memberIdsCsv?.trim();
  if (memberIdsCsv) {
    params.memberIdsCsv = memberIdsCsv;
  }
  const topInviterId = filters.topInviterId?.trim();
  if (topInviterId) {
    params.topInviterId = Number(topInviterId);
  }
  const inviterId = filters.inviterId?.trim();
  if (inviterId) {
    params.inviterId = Number(inviterId);
  }
  const areaCode = filters.areaCode?.trim();
  if (areaCode) {
    params.areaCode = areaCode.replace(/^\+/, '');
  }
  const phoneEmail = filters.phoneEmail?.trim();
  if (phoneEmail) {
    params.phoneEmail = phoneEmail;
  }
  if (filters.assetClass) {
    params.assetClass = filters.assetClass;
  }
  if (filters.overrideMethod) {
    params.overrideMethod = filters.overrideMethod;
  }
  if (filters.periodType) {
    params.periodType = filters.periodType;
  }
  const dailyOrderCount = filters.dailyOrderCount?.trim();
  if (dailyOrderCount) {
    params.dailyOrderCount = Number(dailyOrderCount);
  }
  const minBettingRatio = filters.minBettingRatio?.trim();
  if (minBettingRatio) {
    params.minBettingRatio = minBettingRatio;
  }
  const maxBettingRatio = filters.maxBettingRatio?.trim();
  if (maxBettingRatio) {
    params.maxBettingRatio = maxBettingRatio;
  }

  const orderTimeFrom = inputDateTimeToQueryApi(filters.orderTimeFromInput);
  const orderTimeTo = inputDateTimeToQueryApi(filters.orderTimeToInput);
  if (orderTimeFrom) {
    params.orderTimeFrom = orderTimeFrom;
  }
  if (orderTimeTo) {
    params.orderTimeTo = orderTimeTo;
  }

  const contractTimeFrom = inputDateTimeToQueryApi(filters.contractTimeFromInput);
  const contractTimeTo = inputDateTimeToQueryApi(filters.contractTimeToInput);
  if (contractTimeFrom) {
    params.contractTimeFrom = contractTimeFrom;
  }
  if (contractTimeTo) {
    params.contractTimeTo = contractTimeTo;
  }

  return params;
}

export {
  FUTURES_ASSET_CLASS_OPTIONS,
  FUTURES_OVERRIDE_OPTIONS,
  FUTURES_ROUND_PERIODS,
  FUTURES_ROUND_PERIOD_LABELS,
  MARKET_ASSET_CLASS_LABELS,
};
